import FinanceSection from './components/FinanceSection';
import OperationHistory, {
  getOperationHistorySummaryItems,
  mapPaymentHistoryGroupsToOperationHistoryItems
} from '../OperationHistory';
import PageContentGrid from '@/components/layout/PageContentGrid';
import PageIntro from '@/components/layout/PageIntro';
import PageLoadingState from '@/components/ui/PageLoadingState';
import UpcomingPayments, { getUpcomingPaymentsSummaryGroups, mapUpcomingPaymentsGroupsToViewGroups } from '../UpcomingPayments';
import { Balance, useClientFinanceSummary, useClientPayments, useClientUpcomingPayments } from '@/features/Finance';
import { paths } from '@/app/routes/paths';
import { toast } from '@/components/ui/sonner';
import { useAiChatContext } from '@/features/AiChat/model/context';
import { useAuthStore } from '@/features/Auth/model/authStore';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const Content = () => {
  const navigate = useNavigate();
  const {
    methods: {
      closeChatSession,
      openChat
    }
  } = useAiChatContext();
  const tokenData = useAuthStore((state) => state.tokenData);
  const orgId = tokenData?.org_id ?? null;

  const {
    data: dataUseClientFinanceSummary,
    isError: isErrorUseClientFinanceSummary,
    isLoading: isLoadingUseClientFinanceSummary
  } = useClientFinanceSummary({ orgId });

  const {
    data: dataUseClientUpcomingPayments,
    isError: isErrorUseClientUpcomingPayments,
    isLoading: isLoadingUseClientUpcomingPayments
  } = useClientUpcomingPayments({ orgId });

  const {
    data: dataUseClientPayments,
    isError: isErrorUseClientPayments,
    isLoading: isLoadingUseClientPayments
  } = useClientPayments({ orgId });

  const isBalanceUnavailable = !orgId || !dataUseClientFinanceSummary || isErrorUseClientFinanceSummary;
  const isFinanceLoading =
    isLoadingUseClientFinanceSummary || isLoadingUseClientPayments || isLoadingUseClientUpcomingPayments;
  const operationHistoryItems = mapPaymentHistoryGroupsToOperationHistoryItems(dataUseClientPayments?.groups);
  const operationHistorySummaryItems = getOperationHistorySummaryItems(operationHistoryItems);
  const hasOperationHistorySummaryItems = operationHistorySummaryItems.length > 0;
  const upcomingPaymentGroups = mapUpcomingPaymentsGroupsToViewGroups(dataUseClientUpcomingPayments?.groups);
  const upcomingPaymentSummaryGroups = getUpcomingPaymentsSummaryGroups(upcomingPaymentGroups);
  const hasUpcomingPaymentSummaryItems = upcomingPaymentSummaryGroups.length > 0 && !dataUseClientUpcomingPayments?.allCovered;

  useEffect(() => {
    if (!isErrorUseClientFinanceSummary) return;

    toast.error('Баланс недоступен');
  }, [isErrorUseClientFinanceSummary]);

  useEffect(() => {
    if (!isErrorUseClientUpcomingPayments) return;

    toast.error('Предстоящие оплаты недоступны');
  }, [isErrorUseClientUpcomingPayments]);

  useEffect(() => {
    if (!isErrorUseClientPayments) return;

    toast.error('История операций недоступна');
  }, [isErrorUseClientPayments]);

  const balance = (
    <Balance
      availableAmount={dataUseClientFinanceSummary?.availableBalance ?? null}
      badge={dataUseClientFinanceSummary?.badge ?? 'NONE'}
      currency={dataUseClientFinanceSummary?.currency}
      isError={isBalanceUnavailable}
      nextPaymentAmount={dataUseClientFinanceSummary?.nearestPayment?.amount ?? null}
      nextPaymentDate={dataUseClientFinanceSummary?.nearestPayment?.date ?? null}
      totalDueAmount={dataUseClientFinanceSummary?.totalToPay ?? null}
      variant="detailed"
    />
  );

  const onClickOperationHistory = (): void => {
    navigate(paths.financeOperationHistory);
  };

  const onClickUpcomingPayments = (): void => {
    navigate(paths.financeUpcomingPayments);
  };

  const onClickCreateShipment = (): void => {
    closeChatSession();
    openChat();
  };

  return (
    <>
      <PageIntro title="Финансы" />
      {isFinanceLoading ? (
        <PageLoadingState label="Загрузка финансов" />
      ) : (
        <PageContentGrid aside={balance}>
          <div className="min-[769px]:hidden">
            {balance}
          </div>
          <FinanceSection
            action={hasUpcomingPaymentSummaryItems ? (
              <button
                className="py-2 font-sans text-sm font-semibold leading-5 text-accent transition-colors hover:text-accent/80"
                onClick={onClickUpcomingPayments}
                type="button"
              >
                Показать все
              </button>
            ) : undefined}
            title="Предстоящие оплаты"
          >
            <UpcomingPayments
              allCovered={dataUseClientUpcomingPayments?.allCovered ?? false}
              groups={upcomingPaymentSummaryGroups}
              onCreateShipment={onClickCreateShipment}
              variant="summary"
            />
          </FinanceSection>
          {hasOperationHistorySummaryItems ? (
            <FinanceSection
              action={(
                <button
                  className="py-2 font-sans text-sm font-semibold leading-5 text-accent transition-colors hover:text-accent/80"
                  onClick={onClickOperationHistory}
                  type="button"
                >
                  Вся история
                </button>
              )}
              title="Последние операции"
            >
              <OperationHistory
                items={operationHistorySummaryItems}
                variant="summary"
              />
            </FinanceSection>
          ) : null}
        </PageContentGrid>
      )}
    </>
  );
};

export default Content;